import {
  CheckCircle,
  Edit,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import useAxios from "../../../../../../hooks/useAxios";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import {
  API_DATASETS_AGGREGATION_FUNCTIONS,
  API_DATASETS_METRIC_BY_BOTH_ID,
  API_DATASETS_METRIC_BY_ID,
  API_DATASETS_METRICS,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import AddMetricDialog from "./AddMetricDialog";
import DeleteMetricDialog from "./DeleteMetricDialog";
import { TabsContent } from "../../../../../../shared/components/tabs/Tabs";
import { Input } from "../../../../../../shared/components/input/Input";
import { Button } from "../../../../../../shared/components/button/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import UnexpectedBackendError from "../../../../../../shared/components/UnexpectedBackendError";
import { Label } from "../../../../../../shared/components/label/Label";
import OptionsDropdown from "./OptionsDropdown";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";

const MetricsTabContent = ({ setMetricsLength }) => {
  const { id } = useParams();
  const axios = useAxios();
  const { t } = useTranslation();
  const { display, displayError, displayUnexpectedError } = useToastWrapper();
  const [metrics, setMetrics] = useState([]);
  const [aggregationFunctions, setAggregationFunctions] = useState([]);
  const [isMetricDialogOpen, setIsMetricDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [metricToDelete, setMetricToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);
  const [metricFilter, setMetricFilter] = useState("");

  const openMetricDialog = () => {
    setIsMetricDialogOpen(true);
  };

  const onAddMetricSuccessfully = (newMetric) => {
    setMetrics((prev) => [newMetric, ...prev]);
    setMetricsLength(metrics.length + 1);
    display(
      t("datasetsCentre.common.success"),
      t("datasetsCentre.datasets.metrics.addSuccess", { code: newMetric.code }),
    );
  };

  const checkMetricCodeDuplicated = (code) => {
    return metrics.some((m) => m.code === code.trim());
  };

  const checkMetricColumnNameDuplicated = (columnName, aggregationFunction) => {
    return metrics.some(
      (m) =>
        m.columnName === columnName.trim() &&
        m.aggregationFunction === aggregationFunction.trim(),
    );
  };

  const validateMetricForm = (form, excludeId) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.datasets.metrics.codeRequired");
    } else if (
      metrics.some((d) => d.code === form.code.trim() && d.id !== excludeId)
    ) {
      errors.code = t("datasetsCentre.datasets.metrics.codeExists");
    }

    if (!form.columnName.trim()) {
      errors.columnName = t(
        "datasetsCentre.datasets.metrics.columnNameRequired",
      );
    } else if (
      metrics.some(
        (m) =>
          m.columnName === form.columnName.trim() &&
          m.aggregationFunction === form.aggregationFunction.trim() &&
          m.id !== excludeId,
      )
    ) {
      errors.columnName = t("datasetsCentre.datasets.metrics.columnNameExists");
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.datasets.metrics.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.datasets.metrics.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t(
        "datasetsCentre.datasets.metrics.descriptionEnRequired",
      );
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t(
        "datasetsCentre.datasets.metrics.descriptionEsRequired",
      );
    }

    return errors;
  };

  const startEditingMetric = (metric) => {
    setEditingId(metric.id);
    setEditingData({ ...metric });
  };

  const cancelEditingMetric = () => {
    setEditingId(null);
    setEditingData(null);
    setFormErrors({});
  };

  const saveEditingMetric = async () => {
    if (!editingData) return;

    const errors = validateMetricForm(editingData, editingId);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.put(
        API_DATASETS_METRIC_BY_BOTH_ID(id, editingId),
        editingData,
      );

      if (result.status === 200) {
        const updatedMetric = result.data;
        setMetrics((prev) =>
          prev.map((m) => (m.id === editingId ? updatedMetric : m)),
        );
        setEditingId(null);
        setEditingData(null);
        setFormErrors({});
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.metrics.updateSuccess", {
            code: updatedMetric.code,
          }),
        );
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayDeleteMetricDialog = (metric) => {
    setMetricToDelete(metric);
  };

  const cancelDeleteMetric = () => {
    setMetricToDelete(null);
  };

  const deleteMetric = async (id) => {
    setIsDeleting(true);

    try {
      const result = await axios.delete(API_DATASETS_METRIC_BY_ID(id));

      if (result.status === 204) {
        const metric = metrics.filter((m) => m.id === id)[0];
        setMetrics((prev) => prev.filter((m) => m.id !== id));
        setMetricsLength(metrics.length - 1);
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.metrics.deleteSuccess", {
            code: metric.code,
          }),
        );
      } else {
        console.error(result.data);
        displayError(result.data);
      }
    } catch (error) {
      console.error(error);
      displayUnexpectedError();
    } finally {
      setIsDeleting(false);
      setMetricToDelete(null);
    }
  };

  const filteredMetrics = metrics.filter((dimension) => {
    const searchTerm = metricFilter.toLowerCase();
    return (
      dimension.code.toLowerCase().includes(searchTerm) ||
      dimension.name.en.toLowerCase().includes(searchTerm) ||
      dimension.name.es.toLowerCase().includes(searchTerm)
    );
  });

  useEffect(() => {
    const loadDatasetMetrics = async () => {
      try {
        const response = await axios.get(API_DATASETS_METRICS(id));
        const data = response.data;
        setMetrics(data);
        setMetricsLength(data.length);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      }
    };

    const loadAggregationFunctions = async () => {
      try {
        const response = await axios.get(API_DATASETS_AGGREGATION_FUNCTIONS);
        const data = response.data;
        setAggregationFunctions(data);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      }
    };

    loadDatasetMetrics();
    loadAggregationFunctions();
  }, [id]);

  return (
    <>
      <AddMetricDialog
        datasetId={id}
        aggregationFunctions={aggregationFunctions}
        isMetricDialogOpen={isMetricDialogOpen}
        setIsMetricDialogOpen={setIsMetricDialogOpen}
        onAddMetricSuccessfully={onAddMetricSuccessfully}
        checkMetricCodeDuplicated={checkMetricCodeDuplicated}
        checkMetricColumnNameDuplicated={checkMetricColumnNameDuplicated}
      />
      <DeleteMetricDialog
        metric={metricToDelete}
        onCancel={cancelDeleteMetric}
        isDeleting={isDeleting}
        onDeleteHandler={deleteMetric}
      />
      <TabsContent value="metrics" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              {t("datasetsCentre.datasets.metrics.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("datasetsCentre.datasets.metrics.description")}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t("datasetsCentre.common.filterPlaceholder")}
                value={metricFilter}
                onChange={(e) => setMetricFilter(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button
              onClick={openMetricDialog}
              className="gap-2"
              disabled={editingId !== null}
            >
              <Plus className="w-4 h-4" />
              {t("datasetsCentre.datasets.metrics.addButton")}
            </Button>
          </div>
        </div>

        {metricFilter && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {t("datasetsCentre.datasets.metrics.showingCount", {
                count: filteredMetrics.length,
                total: metrics.length,
              })}
            </span>
            {filteredMetrics.length !== metrics.length && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMetricFilter("")}
                className="h-auto p-1 text-xs"
              >
                {t("common.clearFilter")}
              </Button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredMetrics.map((metric, index) => {
            const isEditing = editingId === metric.id;
            const displayData = isEditing ? editingData : metric;

            return (
              <Card
                key={metric.id}
                className={isEditing ? "ring-2 ring-primary" : ""}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {displayData.code || `Metric #${index + 1}`}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={saveEditingMetric}
                            disabled={isSubmitting}
                            className="text-green-600 hover:text-green-700"
                          >
                            {isSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditingMetric}
                            disabled={isSubmitting}
                            className="text-gray-600 hover:text-gray-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditingMetric(metric)}
                            disabled={editingId !== null}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => displayDeleteMetricDialog(metric)}
                            disabled={editingId !== null}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditing && unexpectedError && <UnexpectedBackendError />}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.code")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.code}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev ? { ...prev, code: e.target.value } : null,
                              )
                            }
                            placeholder="metric_code"
                            className={
                              formErrors.code ? "border-destructive" : ""
                            }
                          />
                          {formErrors.code && (
                            <p className="text-sm text-destructive">
                              {formErrors.code}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.code}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.columnName")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.columnName}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? { ...prev, columnName: e.target.value }
                                  : null,
                              )
                            }
                            placeholder="column_name"
                            className={
                              formErrors.columnName ? "border-destructive" : ""
                            }
                          />
                          {formErrors.columnName && (
                            <p className="text-sm text-destructive">
                              {formErrors.columnName}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.columnName}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2 xl:col-span-1">
                      <Label>
                        {t("datasetsCentre.common.aggregationFunction")} *
                      </Label>
                      {isEditing ? (
                        <OptionsDropdown
                          value={displayData.aggregationFunction}
                          onValueChange={(value) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    aggregationFunction: value,
                                  }
                                : null,
                            )
                          }
                          items={aggregationFunctions}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm capitalize">
                          {
                            aggregationFunctions.filter(
                              (af) =>
                                af.code === displayData.aggregationFunction,
                            )[0]?.name
                          }
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.nameEn")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.name.en}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      name: {
                                        ...prev.name,
                                        en: e.target.value,
                                      },
                                    }
                                  : null,
                              )
                            }
                            placeholder={t(
                              "datasetsCentre.datasets.dimensions.addNameEnPlaceholder",
                            )}
                            className={
                              formErrors.nameEn ? "border-destructive" : ""
                            }
                          />
                          {formErrors.nameEn && (
                            <p className="text-sm text-destructive">
                              {formErrors.nameEn}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.name.en}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.nameEs")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.name.es}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      name: {
                                        ...prev.name,
                                        es: e.target.value,
                                      },
                                    }
                                  : null,
                              )
                            }
                            placeholder={t(
                              "datasetsCentre.datasets.dimensions.addNameEsPlaceholder",
                            )}
                            className={
                              formErrors.nameEs ? "border-destructive" : ""
                            }
                          />
                          {formErrors.nameEs && (
                            <p className="text-sm text-destructive">
                              {formErrors.nameEs}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.name.es}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>
                        {t("datasetsCentre.common.descriptionEn")} *
                      </Label>
                      {isEditing ? (
                        <Textarea
                          value={displayData.description.en}
                          onChange={(e) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    description: {
                                      ...prev.description,
                                      en: e.target.value,
                                    },
                                  }
                                : null,
                            )
                          }
                          placeholder={t(
                            "datasetsCentre.datasets.dimensions.addDescriptionEnPlaceholder",
                          )}
                          rows={2}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[60px]">
                          {displayData.description.en}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t("datasetsCentre.common.descriptionEs")} *
                      </Label>
                      {isEditing ? (
                        <Textarea
                          value={displayData.description.es}
                          onChange={(e) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    description: {
                                      ...prev.description,
                                      es: e.target.value,
                                    },
                                  }
                                : null,
                            )
                          }
                          placeholder={t(
                            "datasetsCentre.datasets.dimensions.addDescriptionEsPlaceholder",
                          )}
                          rows={2}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[60px]">
                          {displayData.description.es}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredMetrics.length === 0 && metricFilter && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">
                {t("datasetsCentre.datasets.metrics.filterZeroResults")}
              </p>
              <Button
                onClick={() => setMetricFilter("")}
                variant="outline"
                className="gap-2 bg-transparent"
              >
                {t("datasetsCentre.common.clearFilter")}
              </Button>
            </CardContent>
          </Card>
        )}

        {metrics.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">
                {t("datasetsCentre.datasets.metrics.noMetrics")}
              </p>
              <Button
                onClick={openMetricDialog}
                variant="outline"
                className="gap-2 bg-transparent"
              >
                <Plus className="w-4 h-4" />
                {t("datasetsCentre.datasets.metrics.addFirstMetric")}
              </Button>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </>
  );
};

export default MetricsTabContent;
